/**
 * EspecificacaoEmbarcacao - Página de Ficha de Especificação (Refatorada)
 * 
 * Esta página foi refatorada para usar componentes modulares em /components/especificacao/
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/axios';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Loader2, ClipboardList, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import PageErrorBoundary from '../components/PageErrorBoundary';

// Importar componentes modulares
import {
  NOVO_ITEM_INICIAL,
  NOVO_SERVICO_INICIAL,
  getCustoItem,
  getCustoTotal,
  getCompradoTotal,
  ComprasAClassificar,
  getProgressoChecklist,
  getItensSemFornecedor,
  agruparPorCategoria,
  HeaderEspecificacao,
  ListaChecklist,
  FotoModelo,
  ResumoEspecificacao,
  DialogAddItem,
  DialogAddProduto,
  DialogImportEspecificacao,
  DialogSolicitarCompra,
  HistoricoFichaDialog
} from '../components/especificacao';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const EspecificacaoEmbarcacao = () => {
  const { projectId, embarcacaoIndex } = useParams();
  const navigate = useNavigate();
  
  // Estados principais
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');
  const [project, setProject] = useState(null);
  const [embarcacao, setEmbarcacao] = useState(null);
  const [vesselModel, setVesselModel] = useState(null);
  const [fornecedores, setFornecedores] = useState([]);
  const [activeTab, setActiveTab] = useState('checklist');
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Estados de edição
  const [checklistItems, setChecklistItems] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [fotoModelo, setFotoModelo] = useState('');
  
  // Estados de permissão
  const [podeVerCustos, setPodeVerCustos] = useState(true);
  // Custo Efetivo do projeto (soma dinâmica das POs aprovadas) — mesma lógica da aba Financeiro
  const [custoEfetivoProjeto, setCustoEfetivoProjeto] = useState(null);
  // Custo Estimado (manual) do projeto — aba Financeiro. Usado para a mesma comparação % mostrada lá.
  const [custoEstimadoProjeto, setCustoEstimadoProjeto] = useState(null);
  // Categorias master (sincronizadas com /api/categorias-produto)
  const [categoriasMaster, setCategoriasMaster] = useState([]);
  // Drag-and-drop reordenacao
  const [dragOverIndex, setDragOverIndex] = useState(null);
  // Confirmacao de exclusao de item
  const [itemParaExcluir, setItemParaExcluir] = useState(null);
  // Confirmacao de exclusao de produto vinculado (custo)
  const [custoParaExcluir, setCustoParaExcluir] = useState(null);
  
  // Estados de dialogs
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [showAddProdutoDialog, setShowAddProdutoDialog] = useState(false);
  const [showAddServicoDialog, setShowAddServicoDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showSolicitarCompraDialog, setShowSolicitarCompraDialog] = useState(false);
  const [itemParaCompra, setItemParaCompra] = useState(null);
  const [showHistoricoDialog, setShowHistoricoDialog] = useState(false);
  
  // Estados de formulários
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);
  const [novoItem, setNovoItem] = useState(NOVO_ITEM_INICIAL);
  const [novoServico, setNovoServico] = useState(NOVO_SERVICO_INICIAL);
  const [buscaProduto, setBuscaProduto] = useState('');
  const [produtosBusca, setProdutosBusca] = useState([]);
  
  // Estados de importação
  const [projectsWithSpecs, setProjectsWithSpecs] = useState([]);
  const [selectedImportProject, setSelectedImportProject] = useState(null);
  const [selectedImportEmbarcacao, setSelectedImportEmbarcacao] = useState(null);
  const [loadingImport, setLoadingImport] = useState(false);

  // Snapshot do checklist como veio do servidor (pra blindar contra perda massiva no auto-save)
  const originalSnapshotRef = useRef(null);

  // Detecta perda massiva de itens_custo (bug pattern observado em prod):
  // se TODOS os itens que tinham produtos vinculados perderam ao mesmo tempo, bloqueia o save.
  const detectarPerdaMassiva = useCallback((items, snapshot) => {
    if (!Array.isArray(snapshot) || snapshot.length === 0) return false;
    const itensComProdutos = snapshot.filter(
      (s) => Array.isArray(s?.itens_custo) && s.itens_custo.length > 0
    );
    if (itensComProdutos.length === 0) return false; // nada a perder
    const safeItems = Array.isArray(items) ? items : [];
    let perdidos = 0;
    for (const original of itensComProdutos) {
      const atual = safeItems.find((i) => {
        if (!i) return false;
        if (original.id && i.id && i.id === original.id) return true;
        if (
          original.numero &&
          i.numero === original.numero &&
          (i.descricao || '') === (original.descricao || '')
        ) return true;
        return false;
      });
      if (atual && (!Array.isArray(atual.itens_custo) || atual.itens_custo.length === 0)) {
        perdidos++;
      }
    }
    // Bloqueia somente quando TODOS os itens-com-produtos foram zerados simultaneamente
    return perdidos > 0 && perdidos === itensComProdutos.length;
  }, []);

  // Valores calculados
  const progresso = getProgressoChecklist(checklistItems);
  const custoTotal = getCustoTotal(checklistItems);
  // Custo Comprado (real) usa a MESMA lógica de soma de POs aprovadas do projeto
  // (aba Financeiro), garantindo consistência em todo o sistema. Fallback para o
  // cálculo antigo por item apenas se o endpoint financeiro ainda não respondeu.
  const compradoTotal = custoEfetivoProjeto ?? getCompradoTotal(checklistItems);
  const itensSemFornecedor = getItensSemFornecedor(checklistItems);
  const categorias = agruparPorCategoria(checklistItems);

  useEffect(() => {
    document.title = "Ficha de Especificação — NautilusOS";
    fetchData();
  }, [projectId, embarcacaoIndex]);

  // Auto-save com debounce
  useEffect(() => {
    if (!hasChanges || !project) return;
    
    const timer = setTimeout(() => {
      handleAutoSave();
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [checklistItems, hasChanges]);

  const fetchData = async () => {
    try {
      const [projectRes, fornecedoresRes, meRes, categoriasRes, financeiroRes] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get('/fornecedores'),
        api.get('/auth/me'),
        api.get('/categorias-produto').catch(() => ({ data: [] })),
        api.get(`/projects/${projectId}/financeiro`).catch(() => ({ data: null }))
      ]);
      
      const projectData = projectRes.data;
      setProject(projectData);
      setFornecedores(fornecedoresRes.data || []);
      setCategoriasMaster((categoriasRes.data || []).map(c => c.nome).filter(Boolean));
      setCustoEfetivoProjeto(financeiroRes.data ? financeiroRes.data.custo_efetivo : null);
      setCustoEstimadoProjeto(financeiroRes.data ? financeiroRes.data.custo_estimado_manual : null);
      
      const userData = meRes.data;
      setPodeVerCustos(userData.pode_ver_custos !== false && userData.role !== 'producao');

      // Producao nao pode editar a ficha — redirecionar para o preview PDF (read-only)
      if (userData.role === 'producao') {
        toast.info('Acesso de leitura: redirecionando para o preview do caderno');
        navigate(`/projects/${projectId}/embarcacao/${embarcacaoIndex}/pdf`, { replace: true });
        return;
      }
      
      const embIndex = parseInt(embarcacaoIndex);
      if (projectData.embarcacoes && projectData.embarcacoes[embIndex]) {
        const emb = projectData.embarcacoes[embIndex];
        setEmbarcacao(emb);
        
        if (emb.foto_modelo) {
          setFotoModelo(emb.foto_modelo);
        }
        
        if (emb.vessel_model_id) {
          try {
            const modelRes = await api.get(`/vessel-models/${emb.vessel_model_id}`);
            setVesselModel(modelRes.data);
            
            const savedChecklist = emb.checklist_preenchido || [];
            const modelChecklist = modelRes.data.checklist_base || [];
            
            let finalChecklist;
            
            if (savedChecklist.length > 0) {
              finalChecklist = [...savedChecklist];
              
              modelChecklist.forEach(modelItem => {
                const exists = savedChecklist.find(s => 
                  s.descricao === modelItem.descricao || s.numero === modelItem.numero
                );
                if (!exists) {
                  finalChecklist.push({
                    numero: modelItem.numero,
                    categoria: modelItem.categoria || 'Geral',
                    descricao: modelItem.descricao || '',
                    tipo_item: modelItem.tipo_item || 'produto',
                    status: false,
                    observacao: modelItem.observacao || '',
                    fornecedor_id: '',
                    quantidade: '',
                    foto_url: ''
                  });
                } else if (exists && !exists.tipo_item && modelItem.tipo_item) {
                  exists.tipo_item = modelItem.tipo_item;
                }
              });
            } else {
              finalChecklist = modelChecklist.map(item => ({
                numero: item.numero,
                categoria: item.categoria || 'Geral',
                descricao: item.descricao || '',
                tipo_item: item.tipo_item || 'produto',
                status: false,
                observacao: item.observacao || '',
                fornecedor_id: '',
                quantidade: '',
                foto_url: ''
              }));
            }
            
            setChecklistItems(finalChecklist);
            originalSnapshotRef.current = JSON.parse(JSON.stringify(finalChecklist));
            
            const cats = [...new Set(finalChecklist.map(i => i.categoria || 'Geral'))];
            const expanded = {};
            cats.forEach(cat => { expanded[cat] = true; });
            setExpandedCategories(expanded);
          } catch (modelError) {
            console.error('Erro ao carregar modelo:', modelError);
            const fallback = emb.checklist_preenchido || [];
            setChecklistItems(fallback);
            originalSnapshotRef.current = JSON.parse(JSON.stringify(fallback));
          }
        } else {
          // Sem vessel_model_id: priorizar dados salvos (checklist_preenchido).
          // Fallback para checklist (template legado) só se não houver dados salvos.
          const saved = emb.checklist_preenchido || [];
          const initial = saved.length > 0 ? saved : (emb.checklist || []);
          setChecklistItems(initial);
          originalSnapshotRef.current = JSON.parse(JSON.stringify(initial));
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados da embarcação');
    } finally {
      setLoading(false);
    }
  };

  // Cria snapshot leve no histórico (fire-and-forget, throttle do backend evita duplicação)
  const criarSnapshotHistorico = useCallback(async (motivo) => {
    if (!projectId || embarcacaoIndex === undefined) return;
    try {
      await api.post(
        `/projects/${projectId}/ficha-historico/${embarcacaoIndex}`,
        { motivo }
      );
    } catch (err) {
      // Falha do histórico nunca atrapalha o fluxo principal — silent
    }
  }, [projectId, embarcacaoIndex]);

  const handleAutoSave = async () => {
    if (!project || saving) return;

    // Trava de segurança: se detectar perda massiva de itens_custo (bug pattern),
    // ABORTA o save e avisa o usuário. Não sobrescreve dados salvos.
    if (detectarPerdaMassiva(checklistItems, originalSnapshotRef.current)) {
      setAutoSaveStatus('Bloqueado: possível perda de dados');
      toast.error(
        'Auto-save bloqueado: detectamos que todos os produtos vinculados desta ficha sumiram simultaneamente. Recarregue a página (F5) para evitar sobrescrever os dados salvos no servidor.',
        { duration: 12000 }
      );
      return;
    }
    
    setAutoSaveStatus('Salvando...');
    try {
      const embIndex = parseInt(embarcacaoIndex);
      const updatedEmbarcacoes = [...project.embarcacoes];
      
      updatedEmbarcacoes[embIndex] = {
        ...updatedEmbarcacoes[embIndex],
        checklist_preenchido: checklistItems,
        progresso_checklist: progresso,
        foto_modelo: fotoModelo,
        ultima_atualizacao: new Date().toISOString()
      };
      
      await api.put(`/projects/${projectId}`, {
        ...project,
        embarcacoes: updatedEmbarcacoes
      });
      
      setProject({ ...project, embarcacoes: updatedEmbarcacoes });
      setAutoSaveStatus('Salvo');
      setHasChanges(false);
      // Atualiza o snapshot com o novo estado salvo (já validado)
      originalSnapshotRef.current = JSON.parse(JSON.stringify(checklistItems));
      // Grava snapshot no histórico (fire-and-forget; backend tem throttle de 5min)
      criarSnapshotHistorico('auto-save');
      
      setTimeout(() => setAutoSaveStatus(''), 2000);
    } catch (error) {
      console.error('Erro no auto-save:', error);
      setAutoSaveStatus('Erro ao salvar');
    }
  };

  const handleSave = async () => {
    if (!project) {
      toast.error('Erro: Projeto não carregado');
      return;
    }

    // Mesma trava do auto-save: bloqueia salvamento manual se detectar perda massiva
    if (detectarPerdaMassiva(checklistItems, originalSnapshotRef.current)) {
      toast.error(
        'Salvamento bloqueado: detectamos que todos os produtos vinculados desta ficha sumiram. Recarregue a página (F5) antes de salvar para evitar sobrescrever dados.',
        { duration: 12000 }
      );
      return;
    }
    
    setSaving(true);
    try {
      const embIndex = parseInt(embarcacaoIndex);
      const updatedEmbarcacoes = [...project.embarcacoes];
      
      updatedEmbarcacoes[embIndex] = {
        ...updatedEmbarcacoes[embIndex],
        checklist_preenchido: checklistItems,
        progresso_checklist: progresso,
        foto_modelo: fotoModelo,
        ultima_atualizacao: new Date().toISOString()
      };
      
      await api.put(`/projects/${projectId}`, {
        ...project,
        embarcacoes: updatedEmbarcacoes
      });
      
      setProject({ ...project, embarcacoes: updatedEmbarcacoes });
      setHasChanges(false);
      // Atualiza o snapshot com o novo estado salvo (já validado)
      originalSnapshotRef.current = JSON.parse(JSON.stringify(checklistItems));
      // Grava snapshot manual no histórico (fire-and-forget)
      criarSnapshotHistorico('save-manual');
      toast.success('Especificações salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar especificações');
    } finally {
      setSaving(false);
    }
  };

  const updateChecklistItem = (index, field, value) => {
    const updated = [...checklistItems];
    updated[index] = { ...updated[index], [field]: value };
    setChecklistItems(updated);
    setHasChanges(true);
  };

  // Drag-and-drop: reordena itens do checklist localmente (nao afeta template)
  const handleDragStartItem = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };
  const handleDragOverItem = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) setDragOverIndex(index);
  };
  const handleDragLeaveItem = () => setDragOverIndex(null);
  const handleDropItem = (e, toIndex) => {
    e.preventDefault();
    setDragOverIndex(null);
    const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (Number.isNaN(fromIndex) || fromIndex === toIndex) return;
    const list = [...checklistItems];
    const [moved] = list.splice(fromIndex, 1);
    moved.ordem_customizada = true; // marca que foi reordenado manualmente
    list.splice(toIndex, 0, moved);
    list.forEach((it, i) => { it.numero = i + 1; });
    setChecklistItems(list);
    setHasChanges(true);
  };

  // Remover item do checklist (apenas neste projeto, nao afeta template)
  const confirmarExcluirItem = (index) => {
    setItemParaExcluir(index);
  };
  const handleExcluirItem = () => {
    if (itemParaExcluir === null) return;
    const list = [...checklistItems];
    const removido = list[itemParaExcluir];
    list.splice(itemParaExcluir, 1);
    list.forEach((it, i) => { it.numero = i + 1; });
    setChecklistItems(list);
    setHasChanges(true);
    // CRITICO: atualiza o snapshot pra refletir a remocao INTENCIONAL.
    // Sem isso, a guarda detectarPerdaMassiva trata a acao como bug e bloqueia o save.
    if (originalSnapshotRef.current && Array.isArray(originalSnapshotRef.current) && removido) {
      originalSnapshotRef.current = originalSnapshotRef.current.filter((s) => {
        if (!s) return false;
        if (s.id && removido.id && s.id === removido.id) return false;
        if (s.numero === removido.numero && (s.descricao || '') === (removido.descricao || '')) return false;
        return true;
      });
    }
    setItemParaExcluir(null);
    toast.success('Item removido deste projeto');
  };

  // Categorias unificadas: master + checklist atual + categoria nova em edicao
  const categoriasUnificadas = useMemo(() => {
    const set = new Set();
    categoriasMaster.forEach(c => c && set.add(c));
    checklistItems.forEach(i => i?.categoria && set.add(i.categoria));
    if (novoItem?.categoria) set.add(novoItem.categoria);
    return Array.from(set).sort();
  }, [categoriasMaster, checklistItems, novoItem?.categoria]);

  // Refresh master apos criar nova categoria
  const refetchCategoriasMaster = async () => {
    try {
      const res = await api.get('/categorias-produto');
      setCategoriasMaster((res.data || []).map(c => c.nome).filter(Boolean));
    } catch (e) { /* silent */ }
  };

  const toggleCategory = (categoria) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoria]: !prev[categoria]
    }));
  };

  // Funções de custo
  const addProdutoToItem = (produto) => {
    const updated = [...checklistItems];
    const item = updated[selectedItemIndex];
    if (!item.itens_custo) item.itens_custo = [];
    
    const novoCusto = {
      id: crypto.randomUUID(),
      tipo: 'produto',
      produto_id: produto.id,
      produto_sku: produto.sku,
      produto_nome: produto.nome,
      quantidade: 1,
      unidade: produto.unidade_medida || 'UN',
      valor_unitario: produto.custo_medio || 0,
      valor_total: produto.custo_medio || 0,
      fornecedor_id: '',
      fornecedor_nome: '',
      observacao: ''
    };
    
    item.itens_custo.push(novoCusto);
    item.custo_total = getCustoItem(item);
    setChecklistItems(updated);
    setHasChanges(true);
    setShowAddProdutoDialog(false);
    toast.success('Produto vinculado!');
  };

  const addServicoToItem = () => {
    if (!novoServico.descricao || !novoServico.valor) return;
    
    const updated = [...checklistItems];
    const item = updated[selectedItemIndex];
    if (!item.itens_custo) item.itens_custo = [];
    
    const fornecedor = fornecedores.find(f => f.id === novoServico.fornecedor_id);
    
    const novoCusto = {
      id: crypto.randomUUID(),
      tipo: 'servico',
      descricao_manual: novoServico.descricao,
      quantidade: 1,
      unidade: 'SV',
      valor_unitario: parseFloat(novoServico.valor) || 0,
      valor_total: parseFloat(novoServico.valor) || 0,
      fornecedor_id: novoServico.fornecedor_id,
      fornecedor_nome: fornecedor?.razao_social || fornecedor?.nome || '',
      observacao: novoServico.observacao
    };
    
    item.itens_custo.push(novoCusto);
    item.custo_total = getCustoItem(item);
    setChecklistItems(updated);
    setHasChanges(true);
    setShowAddServicoDialog(false);
    setNovoServico(NOVO_SERVICO_INICIAL);
    toast.success('Serviço adicionado!');
  };

  const updateItemCusto = (itemIndex, custoIndex, field, value) => {
    const updated = [...checklistItems];
    const item = updated[itemIndex];
    const custo = item.itens_custo[custoIndex];
    
    custo[field] = value;
    
    if (field === 'quantidade' || field === 'valor_unitario') {
      custo.valor_total = (parseFloat(custo.quantidade) || 0) * (parseFloat(custo.valor_unitario) || 0);
    }
    
    item.custo_total = getCustoItem(item);
    setChecklistItems(updated);
    setHasChanges(true);
  };

  const removeItemCusto = (itemIndex, custoIndex) => {
    const item = checklistItems[itemIndex];
    const custo = item?.itens_custo?.[custoIndex];
    if (!custo) return;
    setCustoParaExcluir({
      itemIndex,
      custoIndex,
      itemId: item?.id,
      itemNumero: item?.numero,
      itemDescricao: item?.descricao,
      custoNome: custo?.produto_nome || custo?.descricao_manual || 'este produto',
    });
  };

  const handleConfirmarRemoverCusto = () => {
    if (!custoParaExcluir) return;
    const { itemIndex, custoIndex, itemId, itemNumero, itemDescricao } = custoParaExcluir;
    const updated = [...checklistItems];
    const item = updated[itemIndex];
    if (!item || !Array.isArray(item.itens_custo)) {
      setCustoParaExcluir(null);
      return;
    }
    item.itens_custo.splice(custoIndex, 1);
    item.custo_total = getCustoItem(item);
    setChecklistItems(updated);
    setHasChanges(true);

    // CRITICO: refletir a remocao INTENCIONAL no snapshot pra a guarda
    // detectarPerdaMassiva nao tratar como bug e bloquear o auto-save.
    if (originalSnapshotRef.current && Array.isArray(originalSnapshotRef.current)) {
      const snapItem = originalSnapshotRef.current.find((s) => {
        if (!s) return false;
        if (s.id && itemId && s.id === itemId) return true;
        if (itemNumero && s.numero === itemNumero && (s.descricao || '') === (itemDescricao || '')) return true;
        return false;
      });
      if (snapItem) {
        snapItem.itens_custo = JSON.parse(JSON.stringify(item.itens_custo));
      }
    }
    setCustoParaExcluir(null);
    toast.success('Produto desvinculado deste item');
  };

  // Busca de produtos
  const buscarProdutos = async (query) => {
    if (!query || query.length < 2) {
      setProdutosBusca([]);
      return;
    }
    try {
      const res = await api.get(`/produtos/buscar?q=${encodeURIComponent(query)}&limit=10`);
      setProdutosBusca(res.data || []);
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
    }
  };

  // Adicionar novo item
  const handleAddNovoItem = async () => {
    if (!novoItem.descricao.trim()) {
      toast.error('Informe a descrição do item');
      return;
    }

    // Se a categoria nao existe na master, criar (mantem sincronizacao global)
    const catNova = (novoItem.categoria || '').trim();
    if (catNova) {
      const existeNaMaster = categoriasMaster.some(
        c => c.toLowerCase() === catNova.toLowerCase()
      );
      if (!existeNaMaster) {
        try {
          await api.post('/categorias-produto', { nome: catNova, descricao: '' });
          await refetchCategoriasMaster();
        } catch (err) {
          // 400 = ja existe (case-insensitive backend) — ignorar; outros erros: log
          if (err?.response?.status !== 400) {
            console.error('Erro ao sincronizar categoria nova:', err);
          }
        }
      }
    }

    const maxNumero = Math.max(...checklistItems.map(i => i.numero || 0), 0);
    
    const item = {
      id: crypto.randomUUID(),
      numero: maxNumero + 1,
      categoria: novoItem.categoria,
      descricao: novoItem.descricao,
      tipo_item: novoItem.tipo_item,
      status: false,
      observacao: '',
      foto_url: '',
      fornecedor_id: '',
      quantidade: '',
      custo_manual: 0,
      itens_custo: [],
      custo_total: 0,
      is_item_padrao: false
    };
    
    if (novoItem.adicionar_ao_padrao && vesselModel?.id) {
      try {
        await api.post('/checklist/novo-item', {
          ...item,
          vessel_model_id: vesselModel.id
        }, { params: { adicionar_ao_padrao: true } });
        toast.success('Item adicionado ao checklist padrão!');
      } catch (error) {
        console.error('Erro ao salvar no padrão:', error);
      }
    }
    
    setChecklistItems([...checklistItems, item]);
    setHasChanges(true);
    setShowAddItemDialog(false);
    setNovoItem(NOVO_ITEM_INICIAL);
    toast.success('Item adicionado!');
  };

  // Importação de especificações
  const openImportDialog = async () => {
    setLoadingImport(true);
    try {
      const response = await api.get('/projects-with-specifications');
      // Não filtrar o projeto atual - permitir importar de outras embarcações do mesmo projeto
      setProjectsWithSpecs(response.data || []);
      setShowImportDialog(true);
    } catch (error) {
      console.error('Erro ao carregar projetos:', error);
      toast.error('Erro ao carregar projetos disponíveis');
    } finally {
      setLoadingImport(false);
    }
  };

  const handleImportSpecifications = async () => {
    if (!selectedImportProject || selectedImportEmbarcacao === null) {
      toast.error('Selecione um projeto e uma embarcação');
      return;
    }

    setLoadingImport(true);
    try {
      const response = await api.get(
        `/projects/${selectedImportProject.id}/specifications/${selectedImportEmbarcacao}`
      );
      
      const importedSpecs = response.data;
      
      if (importedSpecs.checklist_preenchido && importedSpecs.checklist_preenchido.length > 0) {
        const importedChecklist = importedSpecs.checklist_preenchido.map(item => ({
          ...item,
          id: item.id || crypto.randomUUID()
        }));
        
        if (importedSpecs.foto_modelo) {
          setFotoModelo(importedSpecs.foto_modelo);
        }
        
        setChecklistItems(importedChecklist);
        setHasChanges(true);
        
        toast.success(`Ficha importada de "${selectedImportProject.name}"!`);
      }
      
      setShowImportDialog(false);
      setSelectedImportProject(null);
      setSelectedImportEmbarcacao(null);
    } catch (error) {
      console.error('Erro ao importar:', error);
      toast.error('Erro ao importar especificações');
    } finally {
      setLoadingImport(false);
    }
  };

  const handleGeneratePDF = () => {
    toast.info('Gerando PDF... Aguarde.');
    navigate(`/projects/${projectId}/embarcacao/${embarcacaoIndex}/pdf`);
  };

  // Loading
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="especificacao-embarcacao-page">
      {/* Header */}
      <HeaderEspecificacao
        project={project}
        embarcacao={embarcacao}
        vesselModel={vesselModel}
        progresso={progresso}
        custoTotal={custoTotal}
        podeVerCustos={podeVerCustos}
        saving={saving}
        autoSaveStatus={autoSaveStatus}
        hasChanges={hasChanges}
        onSave={handleSave}
        onGeneratePDF={handleGeneratePDF}
        onOpenHistorico={() => setShowHistoricoDialog(true)}
      />

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-3">
          {podeVerCustos && (
            <ComprasAClassificar
              project={project}
              embarcacaoIndex={parseInt(embarcacaoIndex)}
              onRefresh={fetchData}
            />
          )}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="checklist" className="flex items-center gap-2">
                <ClipboardList className="h-4 w-4" />
                Checklist ({checklistItems.length})
              </TabsTrigger>
              <TabsTrigger value="foto" className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                Foto do Modelo
              </TabsTrigger>
            </TabsList>

            <TabsContent value="checklist" className="mt-4">
              <ListaChecklist
                checklistItems={checklistItems}
                vesselModel={vesselModel}
                expandedCategories={expandedCategories}
                fornecedores={fornecedores}
                podeVerCustos={podeVerCustos}
                onToggleCategory={toggleCategory}
                onUpdateItem={updateChecklistItem}
                onRemoveItem={confirmarExcluirItem}
                onDragStartItem={handleDragStartItem}
                onDragOverItem={handleDragOverItem}
                onDragLeaveItem={handleDragLeaveItem}
                onDropItem={handleDropItem}
                dragOverIndex={dragOverIndex}
                onOpenAddProduto={(index) => {
                  setSelectedItemIndex(index);
                  setShowAddProdutoDialog(true);
                  setBuscaProduto('');
                  setProdutosBusca([]);
                }}
                onUpdateItemCusto={updateItemCusto}
                onRemoveItemCusto={removeItemCusto}
                onFornecedoresUpdate={async () => {
                  try {
                    const r = await api.get('/fornecedores');
                    setFornecedores(r.data || []);
                  } catch { /* silent */ }
                }}
                onOpenAddItem={() => setShowAddItemDialog(true)}
                onOpenImport={openImportDialog}
                projeto={project}
                embarcacaoIndex={embarcacaoIndex}
                onOpenSolicitarCompra={(item) => {
                  setItemParaCompra(item);
                  setShowSolicitarCompraDialog(true);
                }}
              />
            </TabsContent>

            <TabsContent value="foto" className="mt-4">
              <FotoModelo
                fotoModelo={fotoModelo}
                setFotoModelo={setFotoModelo}
                vesselModel={vesselModel}
                embarcacao={embarcacao}
                setHasChanges={setHasChanges}
              />
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <ResumoEspecificacao
            project={project}
            embarcacao={embarcacao}
            vesselModel={vesselModel}
            progresso={progresso}
            itensSemFornecedor={itensSemFornecedor}
            custoTotal={custoTotal}
            compradoTotal={compradoTotal}
            custoEstimadoProjeto={custoEstimadoProjeto}
            podeVerCustos={podeVerCustos}
            totalItens={checklistItems.length}
            itensConcluidos={checklistItems.filter(i => i?.status).length}
          />
        </div>
      </div>

      {/* Dialogs */}
      <DialogAddItem
        open={showAddItemDialog}
        onClose={() => setShowAddItemDialog(false)}
        novoItem={novoItem}
        setNovoItem={setNovoItem}
        onAdd={handleAddNovoItem}
        vesselModel={vesselModel}
        categorias={categoriasUnificadas}
      />

      <DialogAddProduto
        open={showAddProdutoDialog}
        onClose={() => setShowAddProdutoDialog(false)}
        onAdd={addProdutoToItem}
        buscaProduto={buscaProduto}
        setBuscaProduto={setBuscaProduto}
        produtosBusca={produtosBusca}
        onBuscar={buscarProdutos}
      />

      <DialogImportEspecificacao
        open={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        projects={projectsWithSpecs}
        selectedProject={selectedImportProject}
        selectedEmbarcacao={selectedImportEmbarcacao}
        onSelectProject={setSelectedImportProject}
        onSelectEmbarcacao={setSelectedImportEmbarcacao}
        onImport={handleImportSpecifications}
        loading={loadingImport}
        currentProjectId={projectId}
        currentEmbarcacaoIndex={parseInt(embarcacaoIndex)}
      />

      {/* Dialog Solicitar Compra */}
      <DialogSolicitarCompra
        open={showSolicitarCompraDialog}
        onClose={() => {
          setShowSolicitarCompraDialog(false);
          setItemParaCompra(null);
        }}
        item={itemParaCompra}
        projeto={project}
        embarcacaoIndex={embarcacaoIndex}
        onSuccess={(cotacao) => {
          toast.success(`Cotação ${cotacao.numero} criada! Acesse o módulo de Compras para gerenciá-la.`);
        }}
      />

      {/* Histórico da Ficha */}
      <HistoricoFichaDialog
        open={showHistoricoDialog}
        onClose={() => setShowHistoricoDialog(false)}
        projectId={projectId}
        embarcacaoIndex={embarcacaoIndex}
        onRestoreSuccess={() => {
          // Recarrega a ficha pra refletir os dados restaurados
          fetchData();
        }}
      />

      {/* Confirmacao de exclusao de item */}
      <AlertDialog open={itemParaExcluir !== null} onOpenChange={(o) => !o && setItemParaExcluir(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover item deste projeto?</AlertDialogTitle>
            <AlertDialogDescription>
              {itemParaExcluir !== null && checklistItems[itemParaExcluir] ? (
                <>O item <strong>"{checklistItems[itemParaExcluir]?.descricao}"</strong> será removido apenas desta ficha. O modelo da embarcação não será afetado. Esta ação não pode ser desfeita.</>
              ) : 'Confirme para remover o item.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-remover-item">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleExcluirItem} className="bg-red-600 hover:bg-red-700" data-testid="confirm-remover-item">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmacao de exclusao de produto vinculado (custo) */}
      <AlertDialog open={custoParaExcluir !== null} onOpenChange={(o) => !o && setCustoParaExcluir(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desvincular este produto?</AlertDialogTitle>
            <AlertDialogDescription>
              {custoParaExcluir ? (
                <>O produto <strong>"{custoParaExcluir.custoNome}"</strong> será removido deste item. Você pode vinculá-lo novamente a qualquer momento. Confirme para prosseguir.</>
              ) : 'Confirme para desvincular o produto.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-remover-custo">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmarRemoverCusto} className="bg-red-600 hover:bg-red-700" data-testid="confirm-remover-custo">Desvincular</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default function EspecificacaoEmbarcacaoPage(props) {
  return (
    <PageErrorBoundary pageName="na Ficha de Especificação">
      <EspecificacaoEmbarcacao {...props} />
    </PageErrorBoundary>
  );
}
